// 他参加者の「操作中（未確定）」のプレビューを保持する Zustand store。
// - object.transient.transform: ドラッグ移動 / リサイズ / 回転の途中経過（ゴースト枠）
// - pen.stroke.append: ペン描画の途中経過（描きかけのストローク）
//
// いずれも永続化しない一時情報であり、op.transient（fromUserId 付き）を受信して更新する。
// 対応する確定 op（op.commit）が届いた時点、または一定時間更新が無くなった時点で破棄する。

import { create } from "zustand";
import type {
  TransientPenAppendOp,
  TransientTextCursorOp,
  TransientTransformOp,
} from "@refinement-canvas/domain";

export interface RemoteTransformPreview {
  /** プレビューを発生させている参加者の userId */
  readonly userId: string;
  readonly x?: number | undefined;
  readonly y?: number | undefined;
  readonly w?: number | undefined;
  readonly h?: number | undefined;
  readonly rotation?: number | undefined;
  readonly updatedAt: number;
}

export interface RemotePenPreview {
  readonly userId: string;
  readonly strokeId: string;
  readonly points: ReadonlyArray<{ x: number; y: number; pressure: number }>;
  readonly updatedAt: number;
}

// 他参加者がテキスト / 付箋を編集中のキャレット位置（文字オフセット）。
export interface RemoteTextCaret {
  readonly userId: string;
  readonly objectId: string;
  /** 本文先頭からの文字オフセット（読み取り側で Range を構築して画面位置に変換する）。 */
  readonly cursorIndex: number;
  readonly updatedAt: number;
}

// 描きかけストロークの点数上限。長時間の描画でメモリが際限なく増えるのを防ぐ。
const MAX_PEN_POINTS = 4000;

// テキストキャレットの保持キー（objectId + userId）。objectId は区切り文字を含まない nanoid のため
// ":" 区切りで (objectId, userId) が一意に定まる（キーは保存・照合専用で分解はしない）。
function textCaretKey(objectId: string, userId: string): string {
  return `${objectId}:${userId}`;
}

interface CanvasTransientState {
  /** objectId -> 他者が操作中のプレビュー枠 */
  transforms: Record<string, RemoteTransformPreview>;
  /** strokeId -> 他者が描画中のストローク */
  penStrokes: Record<string, RemotePenPreview>;
  /** `${objectId}:${userId}` -> 他者が編集中のテキストキャレット */
  textCarets: Record<string, RemoteTextCaret>;

  applyRemoteTransform: (userId: string, op: TransientTransformOp) => void;
  appendRemotePen: (userId: string, op: TransientPenAppendOp) => void;
  /** 他参加者のテキスト編集キャレット位置（object.text.cursor transient）を反映する。 */
  applyRemoteTextCursor: (userId: string, op: TransientTextCursorOp) => void;
  /**
   * 確定 op 到達時など、対象オブジェクトのプレビュー枠を破棄する。
   * byUserId を指定した場合、そのユーザーが発生させたゴーストのみ破棄する
   * （別ユーザーが同じオブジェクトを操作中の新しいゴーストを誤って消さないため）。
   */
  clearTransformsForObject: (objectId: string, byUserId?: string) => void;
  /**
   * 確定した strokeId の描きかけストロークだけを破棄する（pen.stroke.commit 到達時）。
   * 確定オブジェクト ID = 描画中 strokeId のため、該当ストロークのみを正確に消せる。
   * ユーザー単位で全消しすると、確定 commit の遅延中に描き始めた次のストロークの
   * ゴーストまで消えてしまう（先のストローク commit が後のプレビューを巻き込む）ため避ける。
   */
  clearPenStroke: (strokeId: string) => void;
  /**
   * 対象オブジェクトのテキストキャレットを破棄する（確定 op 到達時など）。
   * byUserId を指定した場合、そのユーザー分のみ破棄する。
   */
  clearTextCaretsForObject: (objectId: string, byUserId?: string) => void;
  /** updatedAt が古い（olderThanMs 以上更新が無い）エントリを破棄する。 */
  prune: (olderThanMs: number) => void;
  reset: () => void;
}

export const useCanvasTransientStore = create<CanvasTransientState>((set) => ({
  transforms: {},
  penStrokes: {},
  textCarets: {},

  applyRemoteTransform: (userId, op) =>
    set((state) => {
      // 防御的ガード: サーバ側でも検証するが、互換性崩れや不正 payload で受信処理が
      // 落ちないよう、想定外の形状は無視する。
      if (!op || typeof op.objectId !== "string" || !op.transform || typeof op.transform !== "object") {
        return state;
      }
      const prev = state.transforms[op.objectId];
      // 同じオブジェクトを別ユーザーが操作し始めた場合は、過去の差分を引き継がず作り直す。
      const base = prev && prev.userId === userId ? prev : { userId, updatedAt: 0 };
      const next: RemoteTransformPreview = {
        ...base,
        userId,
        ...(op.transform.x !== undefined ? { x: op.transform.x } : {}),
        ...(op.transform.y !== undefined ? { y: op.transform.y } : {}),
        ...(op.transform.w !== undefined ? { w: op.transform.w } : {}),
        ...(op.transform.h !== undefined ? { h: op.transform.h } : {}),
        ...(op.transform.rotation !== undefined ? { rotation: op.transform.rotation } : {}),
        updatedAt: Date.now(),
      };
      return { transforms: { ...state.transforms, [op.objectId]: next } };
    }),

  appendRemotePen: (userId, op) =>
    set((state) => {
      // 防御的ガード: 想定外の形状（points が配列でない等）は無視する。
      if (!op || typeof op.strokeId !== "string" || !Array.isArray(op.points)) {
        return state;
      }
      const prev = state.penStrokes[op.strokeId];
      const prevPoints = prev && prev.userId === userId ? prev.points : [];
      const merged = [...prevPoints, ...op.points];
      const points = merged.length > MAX_PEN_POINTS ? merged.slice(-MAX_PEN_POINTS) : merged;
      const next: RemotePenPreview = {
        userId,
        strokeId: op.strokeId,
        points,
        updatedAt: Date.now(),
      };
      return { penStrokes: { ...state.penStrokes, [op.strokeId]: next } };
    }),

  applyRemoteTextCursor: (userId, op) =>
    set((state) => {
      // 防御的ガード: 想定外の形状（cursorIndex が数値でない等）は無視する。
      if (!op || typeof op.objectId !== "string" || typeof op.cursorIndex !== "number") {
        return state;
      }
      const next: RemoteTextCaret = {
        userId,
        objectId: op.objectId,
        cursorIndex: op.cursorIndex,
        updatedAt: Date.now(),
      };
      return {
        textCarets: { ...state.textCarets, [textCaretKey(op.objectId, userId)]: next },
      };
    }),

  clearTransformsForObject: (objectId, byUserId) =>
    set((state) => {
      const entry = state.transforms[objectId];
      if (!entry) return state;
      // 別ユーザーが同じオブジェクトを操作中の場合、その新しいゴーストは消さない。
      if (byUserId !== undefined && entry.userId !== byUserId) return state;
      const next = { ...state.transforms };
      delete next[objectId];
      return { transforms: next };
    }),

  clearPenStroke: (strokeId) =>
    set((state) => {
      if (!state.penStrokes[strokeId]) return state;
      const next = { ...state.penStrokes };
      delete next[strokeId];
      return { penStrokes: next };
    }),

  clearTextCaretsForObject: (objectId, byUserId) =>
    set((state) => {
      const entries = Object.entries(state.textCarets).filter(([, caret]) => {
        if (caret.objectId !== objectId) return true; // 別オブジェクトは残す
        if (byUserId !== undefined && caret.userId !== byUserId) return true; // 別ユーザーは残す
        return false; // 対象を破棄
      });
      if (entries.length === Object.keys(state.textCarets).length) return state;
      return { textCarets: Object.fromEntries(entries) };
    }),

  prune: (olderThanMs) =>
    set((state) => {
      const now = Date.now();
      const transforms = Object.fromEntries(
        Object.entries(state.transforms).filter(([, v]) => now - v.updatedAt < olderThanMs)
      );
      const penStrokes = Object.fromEntries(
        Object.entries(state.penStrokes).filter(([, v]) => now - v.updatedAt < olderThanMs)
      );
      const textCarets = Object.fromEntries(
        Object.entries(state.textCarets).filter(([, v]) => now - v.updatedAt < olderThanMs)
      );
      const transformsChanged =
        Object.keys(transforms).length !== Object.keys(state.transforms).length;
      const penChanged = Object.keys(penStrokes).length !== Object.keys(state.penStrokes).length;
      const caretsChanged =
        Object.keys(textCarets).length !== Object.keys(state.textCarets).length;
      if (!transformsChanged && !penChanged && !caretsChanged) return state;
      return { transforms, penStrokes, textCarets };
    }),

  reset: () => set({ transforms: {}, penStrokes: {}, textCarets: {} }),
}));
